import { FaClipboardList, FaRunning, FaUser } from "react-icons/fa";
import { Crown, Newspaper } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useHaptics } from "@/hooks/useHaptics";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { BOTTOMNAV_HEIGHT } from "@/lib/layout-constants";

/**
 * BottomNav - iOS Native Tab Bar
 * 
 * IMPORTANTE: Este componente NÃO usa position: fixed
 * porque faz parte do flex container do AppShell.
 * 
 * O AppShell gerencia a posição fixa de todos os elementos.
 */

// iOS Native Tab Bar - 5 tabs lineares sem elevação
// Coroa (Territory) no centro, corredor na posição 4
const navItems = [
  { icon: Newspaper, path: "/dashboard", label: "Feed" },
  { icon: FaClipboardList, path: "/training", label: "Plano" },
  { icon: Crown, path: "/", label: "Territory" },        // Centro - Home
  { icon: FaRunning, path: "/run", label: "Run" },       // Posição 4
  { icon: FaUser, path: "/profile", label: "Perfil" },
];

export const BottomNav = () => {
  const location = useLocation();
  const { medium } = useHaptics();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkModalOpen = () => {
      setIsModalOpen(document.body.classList.contains('ybr-modal-open'));
    };
    
    const observer = new MutationObserver(checkModalOpen);
    observer.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    checkModalOpen();
    
    return () => observer.disconnect();
  }, []);
  
  const { data: nearbyCount = 0 } = useQuery({
    queryKey: ['nearby-count'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const { data: userLocation } = await supabase
          .from('user_locations')
          .select('latitude, longitude')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!userLocation) return 0;

        const { data } = await supabase.functions.invoke('get-nearby-runners', {
          body: {
            user_id: user.id,
            location: { lat: userLocation.latitude, lng: userLocation.longitude },
            filters: { max_distance_km: 5 }
          }
        });

        return data?.count || 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 120000,
    enabled: location.pathname !== '/run' && location.pathname !== '/social'
  });
  
  const handleNavClick = () => {
    medium();
  };
  
  if (isModalOpen) {
    return null;
  }
  
  return (
    <nav 
      data-tabbar="true"
      role="navigation"
      aria-label="Navegação principal"
      className="bg-background border-t border-border/40 flex flex-col"
      style={{
        // NÃO usar position: fixed - AppShell controla a posição
        height: `calc(${BOTTOMNAV_HEIGHT}px + env(safe-area-inset-bottom, 34px))`,
        width: '100%',
        // Safe area integrado como padding bottom
        paddingBottom: 'env(safe-area-inset-bottom, 34px)',
      }}
    >
      {/* iOS Native Tab Bar: Icons only, clean minimal look */}
      <div 
        className="flex items-start justify-around max-w-md mx-auto px-2 w-full"
        style={{ paddingTop: 8, height: BOTTOMNAV_HEIGHT }}
      >
        {navItems.map(({ icon: Icon, path, label }) => {
          const isActive = location.pathname === path;
          
          return (
            <Link
              key={path}
              to={path}
              onClick={handleNavClick}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
              role="tab"
              aria-selected={isActive}
              className="flex flex-col items-center justify-start gap-0 min-w-[44px] min-h-[44px] pt-0 px-1 relative transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
            >
              {/* Icon container - centered, no label for cleaner look */}
              <div className={cn(
                "flex items-center justify-center w-7 h-7 transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
              )}>
                <Icon 
                  className={cn(
                    "transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                    isActive 
                      ? "w-6 h-6 text-primary drop-shadow-[0_0_8px_rgba(251,146,60,0.4)]"
                      : "w-[22px] h-[22px] text-muted-foreground/70"
                  )}
                  aria-hidden="true"
                />
                
                {label === 'Territory' && nearbyCount > 0 && (
                  <Badge 
                    className="absolute -top-0.5 right-0 w-4 h-4 flex items-center justify-center p-0 text-[10px] bg-primary rounded-full"
                    aria-label={`${nearbyCount} ${nearbyCount === 1 ? 'corredor próximo' : 'corredores próximos'}`}
                  >
                    {nearbyCount > 9 ? '9+' : nearbyCount}
                  </Badge>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
